import { FC, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Layer, Source } from 'react-map-gl';
import { createMapSourceAtom } from 'components/map/atoms';

/**
 * The tile's own layer name, and the ids of the layers drawn from it. The click
 * control hit-tests `SURVEY_FILL_LAYER` by id.
 */
const SOURCE_LAYER = 'reference-locations';
export const SURVEY_FILL_LAYER = 'survey-area-fill';

/** Matches the preview colour, since a picked section becomes part of it. */
const SURVEY_COLOR = '#0F62FE';

interface SurveyAreasLayerProps {
  /** Survey areas already in the panel's rows, drawn as picked. */
  pickedIds: number[];
  /**
   * Whether the faint fill is painted. On for the survey method, where the wash
   * shows what is clickable; off when the layer is mounted only for snapping to
   * hit-test — invisible layers still answer `queryRenderedFeatures`, so turning
   * the paint off costs snapping nothing and keeps the basemap uncoloured.
   */
  showFill?: boolean;
  /**
   * Whether picked areas get their own solid-ish wash on top.
   *
   * Off where the caller already paints the picked areas itself: the Unit
   * Builder composes them INTO the unit boundary, which has its own fill, so a
   * second wash over part of that boundary made the picked half read darker than
   * the rest of the same unit. The picked outline is drawn either way, so a
   * picked area still reads as picked.
   */
  showPickedFill?: boolean;
}

/**
 * The survey grid as pickable polygons, from the `reference_locations` tile.
 *
 * The land grid underneath is line geometry, so a click inside a section hits
 * nothing on it — these near-transparent fills are what make it clickable.
 *
 * @param {SurveyAreasLayerProps} props - The picked survey area ids.
 * @return {JSX.Element | null} The survey grid layers, or null until the source
 * is known.
 */
export const SurveyAreasLayer: FC<SurveyAreasLayerProps> = ({
  pickedIds,
  showFill = true,
  showPickedFill = true,
}) => {
  const sourceAtom = useMemo(
    () => createMapSourceAtom('referenceLocations'),
    []
  );
  const source = useAtomValue(sourceAtom);

  if (!source) {
    return null;
  }

  return (
    <Source id="survey-areas" type="vector" url={source}>
      <Layer
        id={SURVEY_FILL_LAYER}
        type="fill"
        source-layer={SOURCE_LAYER}
        paint={{
          'fill-color': SURVEY_COLOR,
          'fill-opacity': showFill ? 0.04 : 0,
        }}
      />
      {/*
        One conditional per layer, not a fragment around both: `Source` injects
        its id into its *direct* children, so a fragment swallows it and mapbox
        rejects the layers for having no source.
      */}
      {pickedIds.length > 0 && (
        <Layer
          id="survey-area-picked-fill"
          type="fill"
          source-layer={SOURCE_LAYER}
          filter={['in', 'reference_location_id', ...pickedIds]}
          paint={{
            'fill-color': SURVEY_COLOR,
            'fill-opacity': showPickedFill ? 0.3 : 0,
          }}
        />
      )}
      {pickedIds.length > 0 && (
        <Layer
          id="survey-area-picked-line"
          type="line"
          source-layer={SOURCE_LAYER}
          filter={['in', 'reference_location_id', ...pickedIds]}
          paint={{ 'line-color': SURVEY_COLOR, 'line-width': 2 }}
        />
      )}
    </Source>
  );
};

export default SurveyAreasLayer;
